/**
 * Which of a period's two closing boundaries a given mutation is judged
 * against — the ONE definition, shared by the API's `TimesheetLockGuard`
 * (enforcement) and the web's week grid (gating the affordance). Keeping it
 * here is what stops a row rendering editable and then 422-ing on save.
 *
 * Split out from `./timesheet-settings` because it needs `TimesheetStatus`
 * and that module is deliberately kept free of even sibling imports.
 *
 * See `docs/timesheet-settlement.md` for the design.
 */

import type { TimesheetLockStage } from './timesheet-settings';
import type { TimesheetStatus } from './timesheet-status';

/** The mutations the period lock gates, as they read in the veto message. */
export const TIMESHEET_LOCK_ACTIONS = [
  'edited',
  'submitted',
  'recalled',
  'rejected',
  'approved',
] as const;
export type TimesheetLockAction = (typeof TIMESHEET_LOCK_ACTIONS)[number];

/**
 * Statuses that carry an entry into the SETTLEMENT stage for edit/submit.
 * Both were already committed to the period before the submission deadline
 * and bounced back:
 *
 *   - `rejected` — an approver asked for a correction. Letting the owner
 *     make it is the entire point of the settlement window.
 *   - `failed` — approval ROUTING failed after a valid submit (no eligible
 *     approver). Not the submitter's doing, and the auto-resubmit cron
 *     re-runs these; without this they would strand at the submission
 *     boundary with no human able to revive them.
 *
 * `draft` is deliberately absent. A draft was never committed to the
 * period, so admitting it would turn settlement into a second submission
 * window — exactly what the deadline exists to prevent.
 *
 * `satisfies` is the drift guard: losing one of these from
 * `TimesheetStatus` fails the typecheck here rather than at runtime.
 */
export const SETTLEABLE_TIMESHEET_STATUSES = [
  'rejected',
  'failed',
] as const satisfies ReadonlyArray<TimesheetStatus>;

/** Whether a row's status lets it be corrected/resubmitted during settlement. */
export function isSettleableStatus(status: TimesheetStatus): boolean {
  return (SETTLEABLE_TIMESHEET_STATUSES as readonly TimesheetStatus[]).includes(status);
}

/**
 * The closing boundary `action` on a row in `status` is judged against.
 *
 * The axis is what the action DOES to the period's contents, not who is
 * doing it — a submitter acts inside the settlement window when fixing a
 * rejection, so keying on the actor's role would get that case wrong:
 *
 *   - Decisions (`approved` / `rejected`) close out work already committed
 *     to the period → always `settlement`.
 *   - `recalled` pulls a submitted row back to draft, WITHDRAWING committed
 *     work → always `submission`. Allowing it during settlement would let a
 *     submitter unilaterally shrink a closed period ("no new submissions"
 *     run in reverse). The loop it would serve is closed a different way:
 *     ask the approver to reject, then correct and resubmit.
 *   - `edited` / `submitted` depend on the ROW: a bounced entry
 *     ({@link isSettleableStatus}) is being corrected, so it settles; a
 *     `draft` is new work, so it closes on the submission deadline.
 *
 * A `switch` over a total union rather than a `Record`, because two of the
 * five arms need the status too — adding an action to
 * {@link TIMESHEET_LOCK_ACTIONS} fails to compile until its stage is
 * stated, so a new mutation can't silently inherit a default.
 */
export function timesheetLockStageFor(
  action: TimesheetLockAction,
  status: TimesheetStatus,
): TimesheetLockStage {
  switch (action) {
    case 'approved':
    case 'rejected':
      return 'settlement';
    case 'recalled':
      return 'submission';
    case 'edited':
    case 'submitted':
      return isSettleableStatus(status) ? 'settlement' : 'submission';
  }
}
